import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { graphqlRequest } from '../services/graphqlClient.js';
import { listRequirements, listStorageLinks } from '../graphql/queries.js';
import { deleteRequirement, deleteStorageLink } from '../graphql/mutations.js';
import { message } from 'antd';
import axios from 'axios';
import googleDriveIcon from '../images/Google_Drive_icon.png';
import sharepointIcon from '../images/Microsoft_Office_SharePoint.png';
import onedriveIcon from '../images/Microsoft_OneDrive_Icon.png';
import { fetchFolderDrawingStats } from '../services/drawingGeometryApi.js';
import { DRAWING_REVIEW_ENABLED } from '../utils/featureFlags.js';
import ExtractionProgressBar, { mapChunkingStageToStep } from './ExtractionProgressBar.jsx';
import { useNotificationsContext } from '../contexts/NotificationsContext.js';
import ProjectService from '../services/projectService';

const IS_DEV_MODE = process.env.NODE_ENV === 'development';

const API_BASE_CLASSIFIER = process.env.REACT_APP_API_BASE_CLASSIFIER || 'http://localhost:8000';


const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;


const Section = styled.div`
  padding: 0 24px;
  margin-bottom: 24px;
  position: relative;
  z-index: 2;
  min-width: 720px;
  width: fit-content;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const ProgressBarWrap = styled.div`
  padding: 0 24px 16px;
  min-width: 720px;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FolderCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  margin-left: 6px;
  text-transform: none;
  letter-spacing: 0;
`;

const FolderCard = styled.div`
  background: #fff;
  border: 1px solid rgba(0,0,0,0.07);
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  overflow: visible;

  &:hover {
    border-color: rgba(233,112,36,0.25);
    box-shadow: 0 2px 12px rgba(233,112,36,0.08);
  }
`;

const FolderCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  user-select: none;
`;

const FolderIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(0,0,0,0.04);
`;

const FolderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FolderName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FolderMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FolderStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p =>
    p.$status === 'indexed'  ? '#22c55e' :
    p.$status === 'indexing' ? '#e97024' :
    p.$status === 'paused'   ? '#f59e0b' :
    p.$status === 'failed'   ? '#dc2626' :
    '#d1d5db'};
  animation: ${p => p.$status === 'indexing' ? pulse : 'none'} 1.5s infinite;
`;

const Chevron = styled.svg`
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.2s ease;
  transform: ${p => p.$open ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const ZonePauseBtn = styled.button`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
  color: #e97024;
  background: none;
  border: 1px solid rgba(233, 112, 36, 0.35);
  border-radius: 5px;
  cursor: pointer;
  padding: 3px 8px;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(233, 112, 36, 0.08);
    color: #c96018;
  }
`;

const DotMenuWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const DotMenuBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #e0e0e0;
    color: #374151;
  }
`;

const DotMenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 200;
  min-width: 140px;
  overflow: hidden;
  display: ${p => p.$open ? 'block' : 'none'};
`;

const DotMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  font-size: 13px;
  font-family: inherit;
  color: ${p => p.$danger ? '#d32f2f' : '#374151'};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:hover {
    background: ${p => p.$danger ? 'rgba(211,47,47,0.05)' : '#fafafa'};
  }
`;

const DocList = styled.div`
  border-top: 1px solid rgba(0,0,0,0.06);
  background: #fafafa;
  border-radius: 0 0 12px 12px;
`;

const DocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  transition: background 0.15s;

  &:last-child { border-bottom: none; }
  &:hover { background: rgba(0,0,0,0.02); }
`;

const DocIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  flex-shrink: 0;
  background: rgba(239,68,68,0.1);
  color: #dc2626;
`;

const DocName = styled.span`
  flex: 1;
  font-size: 12px;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  background: ${p =>
    p.$cat === 'Requirement' ? 'rgba(76,175,80,0.12)' :
    p.$cat === 'Deliverable' ? 'rgba(59,130,246,0.12)' :
    'rgba(255,193,7,0.12)'};
  color: ${p =>
    p.$cat === 'Requirement' ? '#388e3c' :
    p.$cat === 'Deliverable' ? '#2563eb' :
    '#f57c00'};
`;

const LabelChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${(p) =>
    p.$extracting ? 'rgba(234,179,8,0.1)' :
    p.$unknowns > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.1)'};
  color: ${(p) =>
    p.$extracting ? '#a16207' :
    p.$unknowns > 0 ? '#dc2626' : '#059669'};
  border: 1px solid ${(p) =>
    p.$extracting ? 'rgba(234,179,8,0.3)' :
    p.$unknowns > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)'};
`;

const ChipDivider = styled.span`
  opacity: 0.4;
  font-weight: 400;
`;

const DrawingChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(59,130,246,0.25);
  background: rgba(59,130,246,0.08);
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(59,130,246,0.15);
    border-color: rgba(59,130,246,0.4);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  background: rgba(255,255,255,0.7);
  border-radius: 12px;
  border: 1.5px dashed rgba(0,0,0,0.08);
  font-size: 13px;
  color: #9ca3af;
`;


function getFolderType(url) {
  if (!url) return 'google';
  if (url.includes('onedrive.live.com') || url.includes('1drv.ms')) return 'onedrive';
  if (url.includes('sharepoint.com')) return 'sharepoint';
  // Mirrors extractDriveFolder's GOV_PORTAL_* allowlists -- without this, these
  // fall through to 'google' and show the wrong icon/label (confirmed live).
  if (url.includes('texas-sos.appianportalsgov.com') || url.includes('statutes.capitol.texas.gov')) {
    return 'govPortal';
  }
  return 'google';
}

function getFileExt(fileName) {
  return (fileName || '').split('.').pop()?.toUpperCase().slice(0, 4) || 'FILE';
}

function isDrawingFile(file) {
  if (file?.docClass === 'drawing') return true;
  return file?.category === 'Deliverable' && file?.structuralMode === 'drawing_annotation';
}

function countDrawingFiles(files) {
  return (files || []).filter(isDrawingFile).length;
}


export default function LinkedFoldersSection({
  selectedProject,
  onDeleted,
  onAddFolder,
  onOpenDrawingReview,
  notifications = [],
}) {
  const [folders, setFolders]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState({});
  const [fileCache, setFileCache]   = useState({});
  const [openMenu, setOpenMenu]     = useState(null);
  const [drawingStats, setDrawingStats] = useState({});

  const { jobProgress, jobMeta, dismissNotif } = useNotificationsContext();
  // Tracks highest step index reached per jobId — prevents bar from regressing
  // when the chunking service loops back to CHUNKING for a subsequent document.
  const maxStepRef = useRef({});

  const loadFolders = useCallback(async () => {
    if (!selectedProject?.id) return;
    setLoading(true);
    try {
        let allReqs = [];
        let nextToken = null;
        do {
        const res = await graphqlRequest({
            query: listRequirements,
            variables: {
            filter: { projectId: { eq: selectedProject.id } },
            limit: 1000,
            nextToken,
            }
        });
        const { items, nextToken: nt } = res.data?.listRequirements || {};
        if (items) allReqs = allReqs.concat(items);
        nextToken = nt;
        } while (nextToken);

        const classifierReqs = allReqs.filter(req => {
          try {
            const meta = JSON.parse(req.description || '{}');
            return meta.source === 'classifier' || meta.storage?.length > 0;
          } catch { return false; }
        });

        const folderData = await Promise.all(classifierReqs.map(async req => {
        try {
            const meta = JSON.parse(req.description || '{}');
            const linkRes = await graphqlRequest({
            query: listStorageLinks,
            variables: {
                filter: { requirementId: { eq: req.id } },
                limit: 10,
            }
            });
            const links = linkRes.data?.listStorageLinks?.items || [];
            const link = links[0];
            return {
              requirementId: req.id,
              title: req.title,
              folderUrl: link?.url || meta.storage?.[0]?.url || '',
              linkId: link?.id || meta.storage?.[0]?.id || '',
              jobId: meta.jobId || '',
              type: getFolderType(link?.url || meta.storage?.[0]?.url || ''),
              isLegacy: meta.source !== 'classifier',
              files: meta.files || [],
            };
        } catch { return null; }
        }));

        const validFolders = folderData.filter(Boolean);
        setFolders(validFolders);

        const idToken = localStorage.getItem('id_token');
          const fileFetches = await Promise.all(
            validFolders.map(async folder => {
              if (folder.files?.length > 0) {
                return { id: folder.requirementId, files: folder.files };
              }
              if (!folder.jobId) return { id: folder.requirementId, files: [] };
              try {
                const res = await axios.get(
                  `${API_BASE_CLASSIFIER}/classifier/results?jobId=${folder.jobId}`,
                  { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
                );
                const files = (res.data?.files || []).filter(f => f.fileKey !== '__meta__');
                return { id: folder.requirementId, files };
              } catch {
                return { id: folder.requirementId, files: [] };
              }
            })
          );

          const initialCache = {};
          fileFetches.forEach(({ id, files }) => { initialCache[id] = files; });
          setFileCache(initialCache);

    } catch (e) {
        console.error('[LinkedFoldersSection] load error:', e);
    } finally {
        setLoading(false);
    }
    }, [selectedProject?.id]);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  useEffect(() => {
    if (!selectedProject?.id) return undefined;
    let cancelled = false;
    const poll = async () => {
      const updates = {};
      await Promise.all(
        folders.map(async (folder) => {
          const files = fileCache[folder.requirementId] || [];
          if (countDrawingFiles(files) === 0) return;
          const stats = await fetchFolderDrawingStats(selectedProject.id, folder.requirementId);
          updates[folder.requirementId] = stats;
        })
      );
      if (!cancelled && Object.keys(updates).length) {
        setDrawingStats((prev) => ({ ...prev, ...updates }));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [folders, fileCache, selectedProject?.id]);
  useEffect(() => {
    if (onAddFolder) onAddFolder((folder, files = []) => {
      setFolders(prev => [...prev, folder]);
      setFileCache(prev => ({ ...prev, [folder.requirementId]: files }));
    });
  }, [onAddFolder]);
  useEffect(() => {
    const handler = () => setOpenMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const toggleExpand = async (requirementId, jobId) => {
    setExpanded(prev => ({ ...prev, [requirementId]: !prev[requirementId] }));

    if (!fileCache[requirementId] && jobId) {
      try {
        const idToken = localStorage.getItem('id_token');
        const res = await axios.get(
          `${API_BASE_CLASSIFIER}/classifier/results?jobId=${jobId}`,
          { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
        );
        const files = (res.data?.files || []).filter(f => f.fileKey !== '__meta__');
        setFileCache(prev => ({ ...prev, [requirementId]: files }));
      } catch (e) {
        console.error('[LinkedFoldersSection] fetch files error:', e);
        setFileCache(prev => ({ ...prev, [requirementId]: [] }));
      }
    } else if (!fileCache[requirementId]) {
      // Legacy requirement — no jobId, just show open folder option
      setFileCache(prev => ({ ...prev, [requirementId]: [] }));
    }
  };

  const handleDelete = async (folder) => {
    if (!window.confirm(`Remove "${folder.title}" and all its indexed files?`)) return;
    try {
        if (folder.linkId) {
        await graphqlRequest({
            query: deleteStorageLink,
            variables: { input: { id: folder.linkId } }
        });
        }

        try {
        await axios.post(
            `${process.env.REACT_APP_DELETE_REQUIREMENT_FILES_URL}/delete-requirement-files`,
            {
            projectId: selectedProject.id,
            requirementId: folder.requirementId,
            linkId: folder.linkId
            }
        );
        } catch (err) {
        console.error('[LinkedFoldersSection] Failed to delete S3 files:', err);
        message.warning('Folder removed but some indexed files may not have been deleted.');
        }

        await graphqlRequest({
        query: deleteRequirement,
        variables: { input: { id: folder.requirementId } }
        });

        setFolders(prev => prev.filter(f => f.requirementId !== folder.requirementId));
        message.success('Folder removed.');
        onDeleted && onDeleted(folder.requirementId);
    } catch (e) {
        console.error('[LinkedFoldersSection] delete error:', e);
        message.error('Failed to remove folder.');
    }
    };

  // Identify the first folder currently being indexed (status PENDING = chunking in progress).
  // Don't wait on the notification's own status to flip away from PENDING (that
  // happens via a separate async round-trip in NotificationsContext's onError) --
  // check jobProgress directly too, so a confirmed backend FAILED hides the card
  // immediately instead of leaving it stuck on "Processing" until that round-trip
  // lands (or, if it never lands, indefinitely). The notification/toast system
  // already tells the user about the failure; this card just shouldn't linger.
  const activeFolder = folders.find(f => {
    const n = notifications.find(n2 => n2.jobId === f.jobId);
    if (!n || n.status !== 'PENDING' || !n.jobId) return false;
    const progress = jobProgress[n.jobId];
    if (progress?.stage === 'FAILED' || progress?.status === 'FAILED') return false;
    return true;
  });
  const activeNotif    = activeFolder ? notifications.find(n => n.jobId === activeFolder.jobId) : null;
  const activeJobId    = activeNotif?.jobId;
  const activeProgress = activeJobId ? jobProgress[activeJobId] : null;
  const activeMeta     = activeJobId ? jobMeta[activeJobId]     : null;

  if (activeJobId) {
    const raw = mapChunkingStageToStep(activeProgress?.stage, activeProgress?.status);
    maxStepRef.current[activeJobId] = Math.max(maxStepRef.current[activeJobId] ?? 0, raw);
  }
  const activeStepIndex = activeJobId ? (maxStepRef.current[activeJobId] ?? 0) : 0;

  const activeDocName = activeMeta?.folderName || activeFolder?.title || 'Folder';
  const docsInfo = (activeProgress?.completedDocuments != null && activeProgress?.totalDocuments != null)
    ? ` · ${activeProgress.completedDocuments} of ${activeProgress.totalDocuments} documents`
    : '';
  const activeLiveSubtitle = activeProgress?.message
    ? `${activeDocName}${docsInfo} — ${activeProgress.message}`
    : `${activeDocName}${docsInfo}`;

  if (loading && !activeNotif) return null;
  if (folders.length === 0 && !activeNotif) return null;

  return (
    <>
      {activeNotif && (
        <ProgressBarWrap>
          <ExtractionProgressBar
            documentName={activeDocName}
            stepIndex={activeStepIndex}
            isComplete={false}
            liveSubtitle={activeLiveSubtitle}
            isDeliverableOnly={!!activeMeta?.isDeliverableOnly}
            hasDrawingDeliverable={!!activeMeta?.hasDrawingDeliverable}
          />
        </ProgressBarWrap>
      )}
      {folders.length > 0 && (
      <Section>
      <SectionHeader>
        <SectionTitle>
          Linked Folders
          <FolderCount>({folders.length})</FolderCount>
        </SectionTitle>
      </SectionHeader>

      {folders.length === 0 ? (
        <EmptyState>No folders linked yet. Paste a folder URL above to get started.</EmptyState>
      ) : (
        folders.map(folder => {
          const isOpen  = !!expanded[folder.requirementId];
          const files   = fileCache[folder.requirementId] || [];

          const matchedNotif = notifications.find(n => n.jobId === folder.jobId);
          const isExtracting = matchedNotif &&
            matchedNotif.type === 'info' &&
            matchedNotif.status !== 'COMPLETE' &&
            matchedNotif.title?.toLowerCase().includes('extraction');
          const isIndexing = matchedNotif &&
            matchedNotif.status === 'PENDING';
          const isZonePaused = !!matchedNotif?.requiresAction &&
            matchedNotif?.actionType === 'zone_confirm';
          const isExtractionFailed = !!matchedNotif?.requiresAction &&
            matchedNotif?.actionType === 'continue_extraction';
          const isDone = !isIndexing && !isExtracting && !isZonePaused && !isExtractionFailed;

          const statusLabel = isZonePaused
            ? 'Extraction paused'
            : isExtractionFailed
            ? 'Extraction failed'
            : isExtracting
            ? 'Extracting…'
            : isIndexing
            ? 'Indexing…'
            : `${files.length || '—'} docs indexed`;

          const dotStatus = isZonePaused
            ? 'paused'
            : isExtractionFailed
            ? 'failed'
            : (isExtracting || isIndexing ? 'indexing' : 'indexed');
          const drawingCount = countDrawingFiles(files);

          const openReview = (mode) => {
            if (!onOpenDrawingReview || !selectedProject?.id) return;
            onOpenDrawingReview({
              mode,
              requirementId: folder.requirementId,
              linkId: folder.linkId,
              folderTitle: folder.title,
            });
          };

          return (
            <FolderCard key={folder.requirementId}>
              <FolderCardHeader
                $clickable
                onClick={() => toggleExpand(folder.requirementId, folder.jobId)}
              >
                {/* Folder icon */}
                <FolderIconWrap $type={folder.type}>
                  {folder.type === 'govPortal' ? (
                    <DocIcon style={{ width: 22, height: 22, fontSize: 8 }}>GOV</DocIcon>
                  ) : (
                    <img
                      src={
                        folder.type === 'sharepoint' ? sharepointIcon :
                        folder.type === 'onedrive' ? onedriveIcon :
                        googleDriveIcon
                      }
                      alt={
                        folder.type === 'sharepoint' ? 'SharePoint' :
                        folder.type === 'onedrive' ? 'OneDrive' :
                        'Google Drive'
                      }
                      width="22"
                      height="22"
                      style={{ objectFit: 'contain' }}
                    />
                  )}
                </FolderIconWrap>

                {/* Folder info */}
                <FolderInfo>
                  <FolderName>{folder.title}</FolderName>
                  <FolderMeta>
                    {folder.type === 'sharepoint' ? 'SharePoint' :
                     folder.type === 'govPortal' ? 'Government Source' :
                     'Google Drive'}
                    {folder.folderUrl && ` · ${folder.folderUrl.split('?')[0].replace(/https?:\/\/[^/]+/, '')}`}
                  </FolderMeta>
                </FolderInfo>

                {/* Status */}
                <FolderStatus>
                  <StatusDot $status={dotStatus} />
                  {statusLabel}
                </FolderStatus>

                {isZonePaused && (
                  <ZonePauseBtn
                    onClick={(e) => {
                      e.stopPropagation();
                      window.dispatchEvent(
                        new CustomEvent('codiii:zone-confirm', { detail: matchedNotif })
                      );
                    }}
                  >
                    Confirm zone setup
                  </ZonePauseBtn>
                )}

                {IS_DEV_MODE && isExtractionFailed && (
                  <ZonePauseBtn
                    onClick={(e) => {
                      e.stopPropagation();
                      ProjectService.continueExtraction(matchedNotif.actionPayload || {})
                        .then(() => dismissNotif(matchedNotif.notificationId))
                        .catch(err => console.error('[continueExtraction] failed:', err));
                    }}
                  >
                    Continue Extraction
                  </ZonePauseBtn>
                )}

                {drawingCount > 0 && (
                  <>
                    {(() => {
                      const stats = drawingStats[folder.requirementId];
                      if (!stats) return null;
                      const {
                        totalComponents, unknownRulesRemaining,
                        sheetCount, sheetsExtracting, extractionComplete,
                      } = stats;
                      if (sheetsExtracting > 0) {
                        return (
                          <LabelChip $extracting>
                            Extracting {sheetCount - sheetsExtracting}/{sheetCount} sheets…
                          </LabelChip>
                        );
                      }
                      if (!extractionComplete && sheetCount === 0) return null;
                      return (
                        <LabelChip $unknowns={unknownRulesRemaining}>
                          {totalComponents > 0
                            ? <>{totalComponents.toLocaleString()} labels</>
                            : 'Extracted'}
                          {unknownRulesRemaining > 0 ? (
                            <>
                              <ChipDivider>·</ChipDivider>
                              {unknownRulesRemaining} type{unknownRulesRemaining === 1 ? '' : 's'} to confirm
                            </>
                          ) : totalComponents > 0 ? (
                            <>
                              <ChipDivider>·</ChipDivider>
                              ✓ All labeled
                            </>
                          ) : null}
                        </LabelChip>
                      );
                    })()}
                    {DRAWING_REVIEW_ENABLED && onOpenDrawingReview && (
                      <DrawingChip
                        type="button"
                        title="Open drawing review"
                        onClick={(e) => { e.stopPropagation(); openReview('mapping'); }}
                      >
                        {drawingCount} drawing{drawingCount === 1 ? '' : 's'} → Review
                      </DrawingChip>
                    )}
                  </>
                )}

                {/* Chevron */}
                <Chevron
                  $open={isOpen}
                  width="16" height="16"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  onClick={e => { e.stopPropagation(); toggleExpand(folder.requirementId, folder.jobId); }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                </Chevron>

                {/* Three-dot menu */}
                <DotMenuWrap onClick={e => e.stopPropagation()}>
                  <DotMenuBtn onClick={() => setOpenMenu(
                    openMenu === folder.requirementId ? null : folder.requirementId
                  )}>
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="1.5"/>
                      <circle cx="12" cy="12" r="1.5"/>
                      <circle cx="12" cy="19" r="1.5"/>
                    </svg>
                  </DotMenuBtn>
                  <DotMenuDropdown $open={openMenu === folder.requirementId}>
                    {folder.folderUrl && (
                      <DotMenuItem onClick={() => { window.open(folder.folderUrl, '_blank'); setOpenMenu(null); }}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                        Open folder
                      </DotMenuItem>
                    )}
                    {DRAWING_REVIEW_ENABLED && drawingCount > 0 && onOpenDrawingReview && (
                      <DotMenuItem onClick={() => { openReview('mapping'); setOpenMenu(null); }}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10"/>
                        </svg>
                        Mapping Check
                      </DotMenuItem>
                    )}
                    <DotMenuItem $danger onClick={() => { handleDelete(folder); setOpenMenu(null); }}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                      Delete folder
                    </DotMenuItem>
                  </DotMenuDropdown>
                </DotMenuWrap>
              </FolderCardHeader>

              {/* Expanded file list */}
              {isOpen && (
                <DocList>
                  {files.length === 0 ? (
                      <DocItem>
                        {folder.isLegacy ? (
                          <DocName style={{ color: '#9ca3af' }}>
                            No file breakdown available for manually linked folders.{' '}
                            {folder.folderUrl && (
                              <a href={folder.folderUrl} target="_blank" rel="noreferrer"
                                style={{ color: '#e97024', textDecoration: 'underline' }}>
                                Open folder →
                              </a>
                            )}
                          </DocName>
                        ) : (
                          <DocName style={{ color: '#9ca3af' }}>Loading files…</DocName>
                        )}
                      </DocItem>
                    ) : (
                    files.map((file, idx) => (
                      <DocItem key={file.fileKey || idx}>
                        <DocIcon>{getFileExt(file.fileName)}</DocIcon>
                        <DocName title={file.fileName}>{file.fileName}</DocName>
                        <CategoryBadge $cat={file.category || 'Unknown'}>
                          {file.category || 'Unknown'}
                        </CategoryBadge>
                      </DocItem>
                    ))
                  )}
                </DocList>
              )}
            </FolderCard>
          );
        })
      )}
      </Section>
      )}
    </>
  );
}